import "../styles/globals.scss";
import type {AppProps} from "next/app";
import {NextPage} from "next";
import {ReactElement, ReactNode, useEffect, useState} from "react";
import Layout from "../components/common/Layout";
import Head from "next/head";
import {appWithTranslation, useTranslation} from "next-i18next";
import {Libre_Baskerville, Source_Sans_3} from "next/font/google";
import {Hydrate, QueryClient, QueryClientProvider} from "@tanstack/react-query";
import {ReactQueryDevtools} from "@tanstack/react-query-devtools";
import {MobileMenuState, PwaContext} from "../components/providers/PwaContext";
import {useRouter} from "next/router";
import AppTracking from "../components/AppTracking";
import {Languages} from "../data/Languages.config";

const sourceSansPro = Source_Sans_3({
	weight: ["300", "400", "600", "700"],
	style: ["normal", "italic"],
	subsets: ["latin"],
	variable: "--font-source-sans-pro",
	display: "optional"
});

const sourceLibreBaskerville = Libre_Baskerville({
	weight: ["400", "700"],
	style: ["normal", "italic"],
	subsets: ["latin"],
	variable: "--font-libre-baskerville",
	display: "optional"
});

export type NextPageWithLayout = NextPage & {
	getLayout?: (page: ReactElement) => ReactNode;
};

interface MyAppProps extends AppProps {
	Component: NextPageWithLayout;
}

function MyApp(props: MyAppProps) {
	const {Component, pageProps} = props;
	const {t} = useTranslation();
	const router = useRouter();
	const [path, setPath] = useState<string>();
	useEffect(() => {
		if (router.isReady) {
			setPath(router.asPath);
		}
	}, [router.isReady, router.pathname]);

	// Use the layout defined at the page level, if available
	const getLayout =
		Component.getLayout ??
		((page: ReactElement) => <Layout key={0}>{page}</Layout>);
	const WrappedComponent = getLayout(<Component {...pageProps} />);

	const [queryClient] = useState(() => new QueryClient());

	const [mobileMenuState, setMobileMenuState] = useState<MobileMenuState>(
		MobileMenuState.CLOSED
	);
	return (
		<QueryClientProvider client={queryClient}>
			<Hydrate state={pageProps.dehydratedState}>
				<div>
					<style jsx global>{`
						:root {
							--font-source-sans-pro: ${sourceSansPro.style.fontFamily};
							--font-libre-baskerville: ${sourceLibreBaskerville.style
						.fontFamily};
						}
					`}</style>
					<Head>
						<meta httpEquiv="Content-Type" content="text/html; charset=utf-8"/>
						<meta
							name="viewport"
							content="minimum-scale=1, initial-scale=1, width=device-width"
						/>
						<title>
							{t(`pagetitles.${path}`).includes("pagetitles.")
								? t(`pagetitles.default`)
								: t(`pagetitles.${path}`)}
						</title>
						<link rel="icon" type="image/x-icon" href="/images/favicon.ico"/>
						{Languages.map(l =>
							<link key={l.url} rel="alternate" href={`https://www.leon-inmobiliaria.com/${l.url}${router.asPath}`} hrefLang={l.hreflang}/>
						)}
					</Head>
					<AppTracking/>
					<PwaContext.Provider value={{mobileMenuState, setMobileMenuState}}>
						{WrappedComponent}
					</PwaContext.Provider>
				</div>
			</Hydrate>
			<ReactQueryDevtools initialIsOpen={false}/>
		</QueryClientProvider>
	);
}

export default appWithTranslation(MyApp);
